import React, { FC, useEffect, useState } from "react";
import styled from "styled-components";
import { useDispatch } from "react-redux";
import TriangleIcon from "../../assets/images/triangle_icon.svg";
import { vwMobile, vwDesktop, toREM } from "../../helpers/styles";
import { BREAKPOINTS } from "../../helpers/theme";
import { openLanguagePickerModal } from "../../store/modals";
import { useAppSelector } from "../../hooks";
import { useLanguagePickerStaticQuery } from "../../hooks/staticQueries/useLanguagePickerStaticQuery";
import { useIntl } from "../../hooks/useIntl";
import { useStaticLabels } from "../../hooks/useStaticLabels";
import { getTranslations } from "../../helpers/translations";
import { getAssetURL } from "../../helpers/assets";

const LanguagePicker: FC = () => {
  const intl = useIntl();
  const labels = useStaticLabels();
  const languagePickerData = useLanguagePickerStaticQuery();
  const [currentLanguage, setCurrentLanguage] =
    useState<{ flag: string | undefined; label: string }>();
  const dispatch = useDispatch();
  const isOpen = useAppSelector((state) => state.modals.languagePicker?.show);
  const countryLabelId = "language-picker-country-label";
  const selectedLanguageId = "language-picker-selected-language";
  const listboxId = "language-picker-listbox";
  const handleClick = (e) => {
    e.preventDefault();
    dispatch(openLanguagePickerModal());
  };
  const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (
      e.key === "Enter" ||
      e.key === " " ||
      e.key === "ArrowDown" ||
      e.key === "ArrowUp"
    ) {
      e.preventDefault();
      dispatch(openLanguagePickerModal());
    }
  };

  useEffect(() => {
    const {
      directus: { language_selector },
    } = languagePickerData;
    const activeLanguagePickerOption = language_selector
      ?.filter((languagePicker: any) => languagePicker?.locale?.enabled)
      .find(
        (languagePicker: any) => languagePicker?.locale?.code === intl?.locale
      );
    const activeLanguageTranslations = getTranslations(
      activeLanguagePickerOption,
      "translations",
      intl.locale
    );
    setCurrentLanguage({
      label: activeLanguageTranslations?.language_label,
      flag: getAssetURL(activeLanguagePickerOption?.flag_image),
    });
  }, [intl?.locale, languagePickerData]);

  return (
    <Container>
      <Label id={countryLabelId}>{labels?.languageSelectorDropdown}</Label>
      <Button
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={Boolean(isOpen)}
        aria-controls={listboxId}
        aria-labelledby={`${countryLabelId} ${selectedLanguageId}`}
        aria-label={`${labels?.languageSelectorDropdown}, combobox, ${isOpen ? "expanded" : "collapsed"}`}
      >
        <Flag src={currentLanguage?.flag} alt={currentLanguage?.label} />
        <SelectedLanguage id={selectedLanguageId}>{currentLanguage?.label}</SelectedLanguage>
        <Triangle src={TriangleIcon} alt={"triangle"} />
      </Button>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: ${vwMobile(12)};
  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    gap: ${vwDesktop(12)};
    width: ${vwDesktop(190)};
  }
`;

const Label = styled.div`
  color: var(--color-black);
  font-family: ${props => props.theme.fontFamily}, ${props => props.theme.fallBackFontFamily}, sans-serif;
  font-weight: 800;
  font-size: ${toREM(16)}rem;
  letter-spacing: 0;
  line-height: ${toREM(21)}rem;
`;

const Button = styled.button`
  position: relative;
  border: none;
  width: 100%;
  border-bottom: 2px solid var(--color-black);
  background: var(--color-white);
  display: flex;
  cursor: pointer;
  gap: ${vwMobile(10)};
  padding-bottom: ${vwMobile(6)};
  align-items: center;

  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: 2px solid #012604;
    outline-offset: 2px;
  }

  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    gap: ${vwDesktop(10)};
    padding-bottom: ${vwDesktop(6)};
  }
`;

const SelectedLanguage = styled.div`
  color: var(--color-black);
  font-family: ${props => props.theme.fontFamily}, ${props => props.theme.fallBackFontFamily}, sans-serif;
  font-weight: 600;
  font-size: ${toREM(15)}rem;
  letter-spacing: 0;
  line-height: ${toREM(21)}rem;
`;

const Flag = styled.img`
  width: ${vwMobile(20)};
  height: auto;
  object-fit: contain;
  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    width: ${vwDesktop(20)};
    height: auto;
    object-fit: contain;
  }
`;

const Triangle = styled.img`
  position: absolute;
  right: ${vwMobile(1)};
  transform: rotate(90deg);
  width: ${vwMobile(4.5)};
  height: auto;
  object-fit: contain;
  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    right: ${vwDesktop(1)};
    width: ${vwDesktop(4.5)};
    height: auto;
    object-fit: contain;
  }
`;

export default LanguagePicker;
